import type { ImageMeta, ProductShape } from '@didto/domain';
import {
  PRODUCT_SHAPES,
  describeModifierRule,
  formatMinor,
  minorToInput,
  moveInOrder,
} from '@didto/domain';
import {
  Badge,
  Button,
  Field,
  IconButton,
  Input,
  Select,
  Sheet,
  Textarea,
  Toggle,
} from '@didto/ui';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useSession } from '@/app/session';
import { useCommand } from '@/app/useCommand';
import type {
  Availability,
  Category,
  ModifierGroup,
  Product,
  ProductModifierGroup,
  RouteRef,
  Variant,
} from '@/lib/catalog';
import { useOnline } from '@/lib/network';
import { ColorPicker } from './CategorySheet';
import { ImageField, MoneyInput, moneyOrError } from './fields';
import './catalog.css';

const INVALIDATE = [['catalog']];

/*
 * ADR-037: every button here that records something is a command and is disabled while the server
 * cannot be reached; opening a form, typing and choosing stay available.
 */
export function ProductSheet({
  product,
  categories,
  routes,
  variants,
  costs,
  groups,
  assignments,
  availability,
  defaultCategoryId,
  onClose,
}: {
  product: Product | null;
  categories: Category[];
  routes: RouteRef[];
  variants: Variant[];
  costs: Map<string, number>;
  groups: ModifierGroup[];
  assignments: ProductModifierGroup[];
  availability: Availability[];
  defaultCategoryId: string | null;
  onClose: () => void;
}) {
  const s = useSession();
  const online = useOnline();
  const canManage = s.can('catalog.manage');
  const currency = s.me?.business?.currency ?? 'PHP';
  const businessId = s.me?.business?.id ?? '';
  const cmd = useCommand(INVALIDATE);
  const archived = !!product?.archived_at;

  return (
    <Sheet open onClose={onClose} title={product ? product.name : 'New product'}>
      <div className="d-stack catalog__sheet">
        <DetailsForm
          key={product?.id ?? 'new'}
          product={product}
          categories={categories}
          routes={routes}
          currency={currency}
          businessId={businessId}
          defaultCategoryId={defaultCategoryId}
          readOnly={!canManage || archived}
          onCreate={async (v) => {
            await cmd.mutateAsync({ kind: 'product.create', payload: v });
            onClose();
          }}
          onUpdate={async (v) => {
            if (!product) return;
            await cmd.mutateAsync({
              kind: 'product.update',
              payload: { product_id: product.id, ...v },
            });
          }}
        />
        {product && !archived && (
          <>
            <VariantsPanel
              product={product}
              variants={variants}
              costs={costs}
              currency={currency}
              readOnly={!canManage}
            />
            <ModifiersPanel
              product={product}
              groups={groups}
              assignments={assignments}
              readOnly={!canManage}
            />
            <AvailabilityPanel
              product={product}
              variants={variants.filter((v) => !v.archived_at)}
              availability={availability}
              readOnly={!canManage}
            />
          </>
        )}
        {product && canManage && (
          <section className="d-stack d-stack--tight">
            <hr className="d-divider" />
            <h3 className="d-eyebrow">{archived ? 'Archived' : 'Danger zone'}</h3>
            {archived ? (
              <div className="d-spread">
                <span className="d-muted">Archived products are hidden from the POS.</span>
                <Button
                  variant="ghost"
                  disabled={!online}
                  onClick={() =>
                    void cmd
                      .mutateAsync({ kind: 'product.restore', payload: { product_id: product.id } })
                      .then(onClose)
                  }
                  data-testid="restore-product"
                >
                  Restore
                </Button>
              </div>
            ) : (
              <div className="d-spread">
                <span className="d-muted">Archiving keeps history and can be undone.</span>
                <Button
                  variant="danger"
                  disabled={!online}
                  onClick={() =>
                    void cmd
                      .mutateAsync({ kind: 'product.archive', payload: { product_id: product.id } })
                      .then(onClose)
                  }
                  data-testid="archive-product"
                >
                  Archive
                </Button>
              </div>
            )}
          </section>
        )}
      </div>
    </Sheet>
  );
}

/* ── Details ── */
interface DetailValues {
  name: string;
  category_id: string | null;
  production_route_id: string | null;
  description: string | null;
  color: string | null;
  shape: ProductShape | null;
  image_path: string | null;
  image_meta: ImageMeta | null;
}

function DetailsForm({
  product,
  categories,
  routes,
  currency,
  businessId,
  defaultCategoryId,
  readOnly,
  onCreate,
  onUpdate,
}: {
  product: Product | null;
  categories: Category[];
  routes: RouteRef[];
  currency: string;
  businessId: string;
  defaultCategoryId: string | null;
  readOnly: boolean;
  onCreate: (
    v: DetailValues & {
      price_minor: number;
      cost_minor: number | null;
      sku: string | null;
      barcode: string | null;
    },
  ) => Promise<void>;
  onUpdate: (v: DetailValues) => Promise<void>;
}) {
  const online = useOnline();
  const [name, setName] = useState(product?.name ?? '');
  const [categoryId, setCategoryId] = useState(product?.category_id ?? defaultCategoryId ?? '');
  const [routeId, setRouteId] = useState(product?.production_route_id ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [color, setColor] = useState<string | null>(product?.color ?? null);
  const [shape, setShape] = useState<string>(product?.shape ?? '');
  const [image, setImage] = useState<{ path: string | null; meta: ImageMeta | null }>({
    path: product?.image_path ?? null,
    meta: (product?.image_meta as ImageMeta | null) ?? null,
  });
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const values: DetailValues = {
      name: name.trim(),
      category_id: categoryId || null,
      production_route_id: routeId || null,
      description: description.trim() || null,
      color,
      shape: (shape || null) as ProductShape | null,
      image_path: image.path,
      image_meta: image.meta,
    };
    setBusy(true);
    try {
      if (product) {
        await onUpdate(values);
      } else {
        const p = moneyOrError(price, currency, { required: true });
        const c = moneyOrError(cost, currency);
        if (p.error || c.error) {
          setError(p.error ?? c.error);
          return;
        }
        await onCreate({
          ...values,
          price_minor: p.minor as number,
          cost_minor: c.minor,
          sku: sku.trim() || null,
          barcode: barcode.trim() || null,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="d-stack">
      <Field label="Name">
        {(id) => (
          <Input
            id={id}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
            autoFocus={!product}
            disabled={readOnly}
            data-testid="product-name"
          />
        )}
      </Field>
      <div className="catalog__pair">
        <Field label="Category">
          {(id) => (
            <Select
              id={id}
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={readOnly}
              data-testid="product-category"
            >
              <option value="">Uncategorised</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Preparation" help="Where a ticket goes when the order is sent">
          {(id) => (
            <Select
              id={id}
              value={routeId}
              onChange={(e) => setRouteId(e.target.value)}
              disabled={readOnly}
              data-testid="product-route"
            >
              <option value="">No preparation</option>
              {routes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {!product && (
        <>
          <div className="catalog__pair">
            <Field label="Price" error={error}>
              {(id) => (
                <MoneyInput
                  id={id}
                  value={price}
                  onChange={setPrice}
                  currency={currency}
                  required
                  testId="product-price"
                />
              )}
            </Field>
            <Field label="Cost (optional)" help="Seen only by catalog managers">
              {(id) => (
                <MoneyInput
                  id={id}
                  value={cost}
                  onChange={setCost}
                  currency={currency}
                  testId="product-cost"
                />
              )}
            </Field>
          </div>
          <div className="catalog__pair">
            <Field label="SKU (optional)">
              {(id) => (
                <Input
                  id={id}
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  maxLength={64}
                  className="d-mono"
                  data-testid="product-sku"
                />
              )}
            </Field>
            <Field label="Barcode (optional)">
              {(id) => (
                <Input
                  id={id}
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  maxLength={64}
                  className="d-mono"
                  data-testid="product-barcode"
                />
              )}
            </Field>
          </div>
        </>
      )}
      <Field label="Description (optional)">
        {(id) => (
          <Textarea
            id={id}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={2}
            disabled={readOnly}
            data-testid="product-description"
          />
        )}
      </Field>
      <div className="catalog__pair">
        <div className="d-field">
          <span className="d-label">Tile colour</span>
          <ColorPicker
            value={color}
            onChange={setColor}
            ariaLabel="tile colour"
            disabled={readOnly}
          />
        </div>
        <Field label="Tile shape">
          {(id) => (
            <Select
              id={id}
              value={shape}
              onChange={(e) => setShape(e.target.value)}
              disabled={readOnly}
            >
              <option value="">Default</option>
              {PRODUCT_SHAPES.map((sh) => (
                <option key={sh} value={sh}>
                  {sh}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {!readOnly && businessId && (
        <ImageField businessId={businessId} path={image.path} onChange={setImage} />
      )}
      {!readOnly && (
        <div className="d-sheet__actions">
          <Button
            type="submit"
            variant="primary"
            busy={busy}
            disabled={!online}
            data-testid="save-product"
          >
            {product ? 'Save details' : 'Create product'}
          </Button>
        </div>
      )}
    </form>
  );
}

/* ── Variants ── */
interface VariantValues {
  name: string;
  default_name?: string;
  price_minor: number;
  cost_minor: number | null;
  sku: string | null;
  barcode: string | null;
}

function VariantsPanel({
  product,
  variants,
  costs,
  currency,
  readOnly,
}: {
  product: Product;
  variants: Variant[];
  costs: Map<string, number>;
  currency: string;
  readOnly: boolean;
}) {
  const online = useOnline();
  const cmd = useCommand(INVALIDATE);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const live = variants.filter((v) => !v.archived_at);
  const archived = variants.filter((v) => !!v.archived_at);
  const single = live.length === 1 && live[0]?.is_default && live[0].name === null;
  const defaultUnnamed = live.some((v) => v.is_default && v.name === null);

  const move = async (v: Variant, dir: -1 | 1) => {
    const ids = moveInOrder(
      live.map((x) => x.id),
      live.findIndex((x) => x.id === v.id),
      dir,
    );
    if (!ids) return;
    await cmd.mutateAsync({
      kind: 'variant.reorder',
      payload: { product_id: product.id, ordered_ids: ids },
    });
  };

  return (
    <section className="d-stack d-stack--tight" data-testid="variants-panel">
      <div className="d-spread">
        <h3 className="d-eyebrow">{single ? 'Price' : 'Variants'}</h3>
        {!readOnly && !adding && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAdding(true)}
            data-testid="add-variant"
          >
            {single ? 'Add a variant' : 'Add variant'}
          </Button>
        )}
      </div>
      {single && live[0] && !adding && (
        <VariantForm
          key={live[0].id}
          variant={live[0]}
          cost={costs.get(live[0].id) ?? null}
          currency={currency}
          single
          readOnly={readOnly}
          onSave={async (v) => {
            await cmd.mutateAsync({
              kind: 'variant.update',
              payload: {
                variant_id: live[0]!.id,
                price_minor: v.price_minor,
                cost_minor: v.cost_minor,
                sku: v.sku,
                barcode: v.barcode,
              },
            });
          }}
        />
      )}
      {!single && (
        <div className="d-list">
          {live.map((v, i) => (
            <div key={v.id} className="d-row" data-testid={`variant-row-${v.id}`}>
              {editingId === v.id ? (
                <VariantForm
                  variant={v}
                  cost={costs.get(v.id) ?? null}
                  currency={currency}
                  readOnly={false}
                  onCancel={() => setEditingId(null)}
                  onSave={async (val) => {
                    await cmd.mutateAsync({
                      kind: 'variant.update',
                      payload: {
                        variant_id: v.id,
                        name: val.name,
                        price_minor: val.price_minor,
                        cost_minor: val.cost_minor,
                        sku: val.sku,
                        barcode: val.barcode,
                      },
                    });
                    setEditingId(null);
                  }}
                />
              ) : (
                <>
                  <div className="d-row__main">
                    <div className="d-row__title">
                      {v.name ?? 'Default'} {v.is_default && <Badge tone="accent">default</Badge>}
                    </div>
                    <div className="d-row__meta">
                      <span className="d-mono">{formatMinor(v.price_minor, currency)}</span>
                      {costs.has(v.id) && (
                        <>
                          {' · cost '}
                          <span className="d-mono">{formatMinor(costs.get(v.id)!, currency)}</span>
                        </>
                      )}
                      {v.sku && (
                        <>
                          {' · '}
                          <span className="d-mono">{v.sku}</span>
                        </>
                      )}
                      {v.barcode && (
                        <>
                          {' · '}
                          <span className="d-mono">{v.barcode}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {!readOnly && (
                    <div className="d-actions">
                      <IconButton
                        size="sm"
                        variant="quiet"
                        label={`move ${v.name ?? 'default'} up`}
                        disabled={!online || i === 0}
                        onClick={() => void move(v, -1)}
                      >
                        ↑
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="quiet"
                        label={`move ${v.name ?? 'default'} down`}
                        disabled={!online || i === live.length - 1}
                        onClick={() => void move(v, 1)}
                      >
                        ↓
                      </IconButton>
                      {!v.is_default && (
                        <Button
                          size="sm"
                          variant="quiet"
                          disabled={!online}
                          onClick={() =>
                            void cmd.mutateAsync({
                              kind: 'variant.set_default',
                              payload: { variant_id: v.id },
                            })
                          }
                          data-testid={`set-default-${v.id}`}
                        >
                          Make default
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingId(v.id)}
                        data-testid={`edit-variant-${v.id}`}
                      >
                        Edit
                      </Button>
                      {!v.is_default && (
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={!online}
                          onClick={() =>
                            void cmd.mutateAsync({
                              kind: 'variant.archive',
                              payload: { variant_id: v.id },
                            })
                          }
                          data-testid={`archive-variant-${v.id}`}
                        >
                          Archive
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          {archived.map((v) => (
            <div key={v.id} className="d-row" data-testid={`variant-row-${v.id}`}>
              <div className="d-row__main">
                <div className="d-row__title">
                  {v.name ?? 'Default'} <Badge>archived</Badge>
                </div>
                <div className="d-row__meta">
                  <span className="d-mono">{formatMinor(v.price_minor, currency)}</span>
                </div>
              </div>
              {!readOnly && (
                <div className="d-actions">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!online}
                    onClick={() =>
                      void cmd.mutateAsync({
                        kind: 'variant.restore',
                        payload: { variant_id: v.id },
                      })
                    }
                  >
                    Restore
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {adding && (
        <VariantForm
          variant={null}
          cost={null}
          currency={currency}
          needsDefaultName={defaultUnnamed}
          readOnly={false}
          onCancel={() => setAdding(false)}
          onSave={async (val) => {
            await cmd.mutateAsync({
              kind: 'variant.create',
              payload: {
                product_id: product.id,
                name: val.name,
                ...(val.default_name ? { default_name: val.default_name } : {}),
                price_minor: val.price_minor,
                cost_minor: val.cost_minor,
                sku: val.sku,
                barcode: val.barcode,
              },
            });
            setAdding(false);
          }}
        />
      )}
    </section>
  );
}

function VariantForm({
  variant,
  cost,
  currency,
  single,
  needsDefaultName,
  readOnly,
  onSave,
  onCancel,
}: {
  variant: Variant | null;
  cost: number | null;
  currency: string;
  single?: boolean;
  needsDefaultName?: boolean;
  readOnly: boolean;
  onSave: (v: VariantValues) => Promise<void>;
  onCancel?: () => void;
}) {
  const online = useOnline();
  const [name, setName] = useState(variant?.name ?? '');
  const [defaultName, setDefaultName] = useState('');
  const [price, setPrice] = useState(variant ? minorToInput(variant.price_minor) : '');
  const [costText, setCostText] = useState(cost === null ? '' : minorToInput(cost));
  const [sku, setSku] = useState(variant?.sku ?? '');
  const [barcode, setBarcode] = useState(variant?.barcode ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const p = moneyOrError(price, currency, { required: true });
    const c = moneyOrError(costText, currency);
    if (p.error || c.error) {
      setError(p.error ?? c.error);
      return;
    }
    setBusy(true);
    try {
      await onSave({
        name: name.trim(),
        ...(needsDefaultName ? { default_name: defaultName.trim() } : {}),
        price_minor: p.minor as number,
        cost_minor: c.minor,
        sku: sku.trim() || null,
        barcode: barcode.trim() || null,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="d-stack catalog__form"
      data-testid="variant-form"
    >
      {needsDefaultName && (
        <Field
          label="Name for the current price"
          help="The existing price becomes a variant, e.g. Regular"
        >
          {(id) => (
            <Input
              id={id}
              value={defaultName}
              onChange={(e) => setDefaultName(e.target.value)}
              required
              maxLength={60}
              data-testid="variant-default-name"
            />
          )}
        </Field>
      )}
      {!single && (
        <Field label="Variant name" help="e.g. Small, Large, Iced">
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={60}
              autoFocus={!variant}
              data-testid="variant-name"
            />
          )}
        </Field>
      )}
      <div className="catalog__pair">
        <Field label="Price" error={error}>
          {(id) => (
            <MoneyInput
              id={id}
              value={price}
              onChange={setPrice}
              currency={currency}
              required
              testId="variant-price"
            />
          )}
        </Field>
        <Field label="Cost (optional)">
          {(id) => (
            <MoneyInput
              id={id}
              value={costText}
              onChange={setCostText}
              currency={currency}
              testId="variant-cost"
            />
          )}
        </Field>
      </div>
      <div className="catalog__pair">
        <Field label="SKU (optional)">
          {(id) => (
            <Input
              id={id}
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              maxLength={64}
              className="d-mono"
              data-testid="variant-sku"
            />
          )}
        </Field>
        <Field label="Barcode (optional)">
          {(id) => (
            <Input
              id={id}
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              maxLength={64}
              className="d-mono"
              data-testid="variant-barcode"
            />
          )}
        </Field>
      </div>
      {!readOnly && (
        <div className="d-sheet__actions">
          {onCancel && (
            <Button variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            busy={busy}
            disabled={!online}
            data-testid="save-variant"
          >
            {variant ? 'Save' : 'Add'}
          </Button>
        </div>
      )}
    </form>
  );
}

/* ── Modifier groups on this product ── */
function ModifiersPanel({
  product,
  groups,
  assignments,
  readOnly,
}: {
  product: Product;
  groups: ModifierGroup[];
  assignments: ProductModifierGroup[];
  readOnly: boolean;
}) {
  const online = useOnline();
  const cmd = useCommand(INVALIDATE);
  const [pick, setPick] = useState('');
  const assigned = assignments
    .map((a) => groups.find((g) => g.id === a.modifier_group_id))
    .filter((g): g is ModifierGroup => !!g);
  const unassigned = groups.filter((g) => !assignments.some((a) => a.modifier_group_id === g.id));
  const move = async (index: number, dir: -1 | 1) => {
    const ids = moveInOrder(
      assigned.map((g) => g.id),
      index,
      dir,
    );
    if (!ids) return;
    await cmd.mutateAsync({
      kind: 'product.reorder_modifier_groups',
      payload: { product_id: product.id, ordered_ids: ids },
    });
  };
  return (
    <section className="d-stack d-stack--tight" data-testid="modifiers-panel">
      <h3 className="d-eyebrow">Modifiers</h3>
      {assigned.length === 0 && <p className="d-muted">No modifier groups on this product.</p>}
      {assigned.length > 0 && (
        <div className="d-list">
          {assigned.map((g, i) => (
            <div key={g.id} className="d-row" data-testid={`assigned-group-${g.id}`}>
              <div className="d-row__main">
                <div className="d-row__title">{g.name}</div>
                <div className="d-row__meta">{describeModifierRule(g)}</div>
              </div>
              {!readOnly && (
                <div className="d-actions">
                  <IconButton
                    size="sm"
                    variant="quiet"
                    label={`move ${g.name} up`}
                    disabled={!online || i === 0}
                    onClick={() => void move(i, -1)}
                  >
                    ↑
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="quiet"
                    label={`move ${g.name} down`}
                    disabled={!online || i === assigned.length - 1}
                    onClick={() => void move(i, 1)}
                  >
                    ↓
                  </IconButton>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={!online}
                    onClick={() =>
                      void cmd.mutateAsync({
                        kind: 'product.unassign_modifier_group',
                        payload: { product_id: product.id, modifier_group_id: g.id },
                      })
                    }
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {!readOnly && unassigned.length > 0 && (
        <div className="catalog__assign">
          <Select
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            aria-label="modifier group to add"
            data-testid="assign-group-select"
          >
            <option value="">Add a modifier group…</option>
            {unassigned.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            variant="ghost"
            disabled={!online || !pick}
            onClick={() =>
              void cmd
                .mutateAsync({
                  kind: 'product.assign_modifier_group',
                  payload: { product_id: product.id, modifier_group_id: pick },
                })
                .then(() => setPick(''))
            }
            data-testid="assign-group"
          >
            Add
          </Button>
        </div>
      )}
    </section>
  );
}

/* ── Availability per location ── */
function AvailabilityPanel({
  product,
  variants,
  availability,
  readOnly,
}: {
  product: Product;
  variants: Variant[];
  availability: Availability[];
  readOnly: boolean;
}) {
  const s = useSession();
  const online = useOnline();
  const cmd = useCommand(INVALIDATE);
  const [reason, setReason] = useState('');
  const locations = s.me?.locations ?? [];
  const isOff = (locationId: string, variantId: string) =>
    availability.some(
      (a) => a.location_id === locationId && a.product_variant_id === variantId && !a.is_available,
    );
  const set = (
    locationId: string,
    target: { product_id: string } | { variant_id: string },
    on: boolean,
  ) =>
    cmd.mutateAsync({
      kind: 'catalog.set_availability',
      payload: {
        location_id: locationId,
        ...target,
        is_available: on,
        reason: on ? null : reason.trim() || null,
      },
    });
  return (
    <section className="d-stack d-stack--tight" data-testid="availability-panel">
      <h3 className="d-eyebrow">Availability</h3>
      {locations.map((loc) => {
        const offCount = variants.filter((v) => isOff(loc.id, v.id)).length;
        const allOff = variants.length > 0 && offCount === variants.length;
        const reasonText = availability.find(
          (a) => a.location_id === loc.id && !a.is_available && a.reason,
        )?.reason;
        return (
          <div key={loc.id} className="d-stack d-stack--tight">
            <div className="d-spread" data-testid={`availability-${loc.id}`}>
              <Toggle
                checked={!allOff}
                disabled={!online || readOnly}
                label={
                  allOff
                    ? `Sold out at ${loc.name}${reasonText ? ` (${reasonText})` : ''}`
                    : offCount > 0
                      ? `Partly available at ${loc.name}`
                      : `Available at ${loc.name}`
                }
                onChange={(on) => void set(loc.id, { product_id: product.id }, on)}
              />
            </div>
            {variants.length > 1 && (
              <div className="d-actions">
                {variants.map((v) => {
                  const off = isOff(loc.id, v.id);
                  return (
                    <Button
                      key={v.id}
                      size="sm"
                      variant="ghost"
                      aria-pressed={!off}
                      disabled={!online || readOnly}
                      onClick={() => void set(loc.id, { variant_id: v.id }, off)}
                      data-testid={`availability-${loc.id}-${v.id}`}
                    >
                      <span
                        className={`d-dot catalog__avail-dot${off ? ' catalog__avail-dot--off' : ''}`}
                        aria-hidden
                      />
                      {v.name ?? 'Default'}: {off ? 'sold out' : 'available'}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <Field label="Reason when marking sold out (optional)">
          {(id) => (
            <Input
              id={id}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              placeholder="e.g. out of oat milk"
              data-testid="availability-reason"
            />
          )}
        </Field>
      )}
    </section>
  );
}
